import './ScrollArea.css';
import type { ComponentChildren } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';

/** Filhos que já são parada de Tab: com um deles, a área não precisa ser focável (axe `scrollable-region-focusable`). */
const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'summary',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/** Máscara de continuação padrão (listas de candidatos, §3.9). */
export const SCROLL_FADE_DEFAULT = 36;

export interface ScrollAreaProps {
  children: ComponentChildren;
  /**
   * Nome acessível vindo do dono (§3.9 Microcopy; §15.4.2: "Lista de atualizações" no `UpdatesFeed`, nos demais o
   * título do cartão-pai). Só é aplicado quando a área vira região rolável focável.
   */
  label: string;
  /** Altura da máscara, px (§3.9): 36 listas de candidatos, 48 feed, 28 cartão no `tablet`/`land`, 26 deputados. */
  fade?: number;
  /**
   * Quando a área rolável vira parada de Tab (`role="region"`, `tabindex="0"`, `aria-label`): `auto` só se nenhum
   * filho for focável (§3.9, §15.4.2); `always` sempre que rolar (§7.15: o feed mistura itens com e sem botão).
   */
  region?: 'auto' | 'always';
  /** Continua rolando no `flow` (ex.: resultados da busca). Padrão: no `flow` vira contêiner comum (§3.9). */
  scrollInFlow?: boolean;
  /** Classes extras do dono (altura, `flex`). */
  class?: string;
}

interface Metrics {
  /** Há conteúdo abaixo da borda: máscara ligada. */
  over: boolean;
  scrollable: boolean;
  focusableChild: boolean;
}

const EMPTY: Metrics = { over: false, scrollable: false, focusableChild: false };

/**
 * Medidas de §3.9: rola quando o conteúdo passa da altura visível; a máscara sai quando
 * `scrollTop + clientHeight ≥ scrollHeight − 1` (a tolerância de 1 px absorve o arredondamento do zoom).
 */
export function scrollMetrics(el: HTMLElement): { over: boolean; scrollable: boolean } {
  const { scrollTop, clientHeight, scrollHeight } = el;
  return {
    scrollable: scrollHeight - clientHeight > 1,
    over: scrollTop + clientHeight < scrollHeight - 1,
  };
}

/**
 * Área rolável com máscara (§3.9): `overflow-y: auto` com `overscroll-behavior: contain`, esmaecimento de `fade` px
 * embaixo enquanto há conteúdo abaixo (`data-state="over"`, atualizado por `scroll` e `ResizeObserver`) e barra fina no
 * desktop (M12). Se rola e nenhum filho é focável, vira região nomeada e focável para rolar por teclado (setas,
 * PageUp/PageDown nativos).
 */
export function ScrollArea({
  children,
  label,
  fade = SCROLL_FADE_DEFAULT,
  region = 'auto',
  scrollInFlow = false,
  class: extra,
}: ScrollAreaProps) {
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [m, setM] = useState<Metrics>(EMPTY);

  const update = useRef<() => void>(() => undefined);
  update.current = () => {
    const el = ref.current;
    if (!el) return;
    const { over, scrollable } = scrollMetrics(el);
    const focusableChild = el.querySelector(FOCUSABLE) !== null;
    setM((prev) =>
      prev.over === over && prev.scrollable === scrollable && prev.focusableChild === focusableChild
        ? prev
        : { over, scrollable, focusableChild },
    );
  };

  // Depois de cada render (filhos novos). Com ResizeObserver, as medidas vêm do aviso dele (depois do layout, sem
  // forçá-lo; filhos novos mudam o tamanho do conteúdo observado): medir aqui forçava o layout da página inteira na
  // tarefa da montagem (36% da tarefa da lista de Atualizações no perfil do boot; DECISOES §49). Aqui fica só o que não
  // é medida: se há filho focável. Sem ResizeObserver, mede já.
  useLayoutEffect(() => {
    if (typeof ResizeObserver !== 'function') {
      update.current();
      return;
    }
    const el = ref.current;
    if (!el) return;
    const focusableChild = el.querySelector(FOCUSABLE) !== null;
    setM((prev) => (prev.focusableChild === focusableChild ? prev : { ...prev, focusableChild }));
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const on = () => update.current();
    el.addEventListener('scroll', on, { passive: true });
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(on);
      ro.observe(el);
      if (contentRef.current) ro.observe(contentRef.current);
    }
    return () => {
      el.removeEventListener('scroll', on);
      ro?.disconnect();
    };
  }, []);

  const asRegion = m.scrollable && (region === 'always' || !m.focusableChild);
  const cls = ['scroll-area', scrollInFlow && 'scroll-area--flow-scroll', extra].filter(Boolean).join(' ');
  // Papel, parada de Tab e nome entram juntos e só quando rola (axe scrollable-region-focusable; §3.9, §15.4.2).
  const regionAttrs = asRegion ? { role: 'region' as const, tabIndex: 0, 'aria-label': label } : {};

  return (
    <div
      ref={ref}
      class={cls}
      data-testid="scroll-area"
      data-state={m.over ? 'over' : undefined}
      style={{ '--fade': `${fade}px` }}
      {...regionAttrs}
    >
      <div ref={contentRef} class="scroll-area__content">
        {children}
      </div>
    </div>
  );
}
